import axios from "axios";
import { env } from "@/config/env";
import { apiRoutes } from "@/config/routes";
import { apiClient } from "@/services/apiClient";
import { authHttp } from "@/services/authHttp";
import { parseApiErrorDetails } from "@/services/parseApiErrorMessage";

export type AdminLoginStartRequest = {
  email: string;
  password: string;
};

export type AdminLoginStartResponse = {
  otp_sent_to: string;
  expires_in_seconds: number;
  max_attempts: number;
  max_resends: number;
};

export type AdminLoginResendResponse = {
  otp_sent_to: string;
  resends_remaining: number;
};

export class AdminAuthConfigError extends Error {
  constructor() {
    super(
      "API base URL is not configured. Set REACT_APP_API_BASE_URL in your .env file.",
    );
    this.name = "AdminAuthConfigError";
  }
}

export type AdminForgotPasswordRequest = {
  email: string;
};

export type AdminForgotPasswordResponse = {
  message: string;
  hint?: string;
};

/** Request a password reset email (no auth required). */
export async function forgotAdminPassword(
  payload: AdminForgotPasswordRequest,
): Promise<AdminForgotPasswordResponse> {
  if (!env.apiBaseUrl) {
    throw new AdminAuthConfigError();
  }

  const { data } = await authHttp.post<AdminForgotPasswordResponse>(
    apiRoutes.admin.auth.passwordForgot,
    {
      email: payload.email.trim(),
    },
  );

  return data;
}

export type AdminResetPasswordRequest = {
  token: string;
  new_password: string;
};

export type AdminResetPasswordResponse = {
  message: string;
  next_step?: string;
};

/** Set a new password using the token from the reset email (no auth required). */
export async function resetAdminPassword(
  payload: AdminResetPasswordRequest,
): Promise<AdminResetPasswordResponse> {
  if (!env.apiBaseUrl) {
    throw new AdminAuthConfigError();
  }

  const { data } = await authHttp.post<AdminResetPasswordResponse>(
    apiRoutes.admin.auth.passwordReset,
    {
      token: payload.token.trim(),
      new_password: payload.new_password,
    },
  );

  return data;
}

/** Step 1: verify password and trigger OTP email (sets login session cookie). */
export async function startAdminLogin(
  payload: AdminLoginStartRequest,
): Promise<AdminLoginStartResponse> {
  if (!env.apiBaseUrl) {
    throw new AdminAuthConfigError();
  }

  const { data } = await authHttp.post<AdminLoginStartResponse>(
    apiRoutes.admin.auth.loginStart,
    {
      email: payload.email.trim(),
      password: payload.password,
    },
  );

  return data;
}

/** Resend OTP during login (requires login session cookie from step 1). */
export async function resendAdminLogin(): Promise<AdminLoginResendResponse> {
  if (!env.apiBaseUrl) {
    throw new AdminAuthConfigError();
  }

  const { data } = await authHttp.post<AdminLoginResendResponse>(
    apiRoutes.admin.auth.loginResend,
    {},
  );

  return data;
}

export type AdminLoginVerifyRequest = {
  code: string;
  remember_device?: boolean;
};

export type AdminLoginVerifyResponse = {
  user: {
    id: string;
    email: string;
    persona: string;
    full_name: string;
    mfa_enabled: boolean;
  };
  access_token: string;
  expires_in: number;
};

export class AdminOtpVerifyError extends Error {
  readonly errorCode: string | null;
  readonly attemptsRemaining: number | null;

  constructor(
    message: string,
    options: {
      errorCode?: string | null;
      attemptsRemaining?: number | null;
    } = {},
  ) {
    super(message);
    this.name = "AdminOtpVerifyError";
    this.errorCode = options.errorCode ?? null;
    this.attemptsRemaining = options.attemptsRemaining ?? null;
  }
}

/** Step 2: verify OTP (requires login session cookie from step 1). */
export async function verifyAdminLogin(
  payload: AdminLoginVerifyRequest,
): Promise<AdminLoginVerifyResponse> {
  if (!env.apiBaseUrl) {
    throw new AdminAuthConfigError();
  }

  try {
    const { data } = await axios.post<AdminLoginVerifyResponse>(
      apiRoutes.admin.auth.loginVerify,
      {
        code: payload.code.trim(),
        remember_device: payload.remember_device ?? false,
      },
      {
        baseURL: env.apiBaseUrl,
        withCredentials: true,
        headers: { Accept: "application/json" },
      },
    );

    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const details = parseApiErrorDetails(
        error.response?.data,
        "Verification failed. Please try again.",
      );
      throw new AdminOtpVerifyError(details.message, {
        errorCode: details.errorCode,
        attemptsRemaining: details.attemptsRemaining,
      });
    }

    throw error instanceof Error
      ? error
      : new Error("Verification failed. Please try again.");
  }
}

export type AdminRole = "admin" | "super_admin" | null;

export type AdminMeResponse = {
  user: {
    id: string;
    email: string;
    persona: string;
    full_name: string;
    mfa_enabled: boolean;
    role: AdminRole;
    phone?: string | null;
  };
};

/**
 * The caller's own profile — this is the only place the FE learns its own role. Login/verify
 * deliberately doesn't carry `role` (see `AdminLoginVerifyResponse` above), so anything gating UI
 * on "am I a super_admin" should call this rather than trust anything cached at login time.
 */
export async function fetchAdminMe(): Promise<AdminMeResponse> {
  const { data } = await apiClient.get<AdminMeResponse>(apiRoutes.admin.auth.me);
  return data;
}

export type AdminAuthRefreshResponse = {
  access_token: string;
  expires_in: number;
};

/** Rotate refresh cookie and mint a new access token (refresh cookie auth). */
export async function refreshAdminAccessToken(): Promise<AdminAuthRefreshResponse> {
  if (!env.apiBaseUrl) {
    throw new AdminAuthConfigError();
  }

  const { data } = await authHttp.post<AdminAuthRefreshResponse>(
    apiRoutes.admin.auth.refresh,
  );

  return data;
}
